import axios, { AxiosError } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../auth/msalConfig';
import { AppRoutes } from '@routes/appRoutes';

axios.interceptors.request.use(async (config) => {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

    // No active account yet: let the request through, ProtectedRoute owns the login UX
    if (!account) {
        return config;
    }

    try {
        const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        // Backend expects an access token, not the ID token — requires a resource scope in
        // REACT_APP_ENTRA_SCOPES (e.g. api://<client-id>/access_as_user), see AGENTS.md
        config.headers.Authorization = `Bearer ${result.accessToken}`;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect(loginRequest);
        }
    }

    return config;
});

// Guards against every in-flight request 401ing at once and triggering the sign-out multiple times
let isSigningOut = false;

// Global sign-out (SLO): unlike acquireTokenRedirect, logoutRedirect does not silently reuse
// the still-valid Entra SSO cookie — it clears it server-side via the end_session_endpoint,
// so it does not re-trigger the same 401 (no loop, see msal-react SKILL.md rule 10). This also
// signs the user out of any other portal relying on the same Entra SSO session, per client request.
const signOutAndRedirectToLogin = async () => {
    if (isSigningOut) {
        return;
    }
    isSigningOut = true;

    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    window.sessionStorage.clear();

    await msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri: AppRoutes.LOGIN_PAGE,
    });
};

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        if (error.response?.status === 401) {
            await signOutAndRedirectToLogin();
        }
        return Promise.reject(error);
    },
);
